import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  useTheme,
  Stack,
  Chip,
  styled,
} from "@mui/material";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import { Devices, DevicesOtherOutlined } from "@mui/icons-material";
import { del } from "../services/apiMethods";
import URLS from "../services/urlConstants";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import DeleteModal from "./DeleteModal";
import ShowComponent from "./ShowComponent";

const TableCellStyled = styled(TableCell)({
  fontWeight: "bold",
  border: "2px solid #ccc",
});

// Sample data (replace with your actual data)
const data = {
  stockSummary: {
    totalItemsAsPerSystem: 5,
    totalItemsScanned: 1,
    totalMissingItems: [
      { msn: "TEST5", itemDescription: "M1 MAC PRO", palletCode: "GP1" },
      { msn: "TEST6", itemDescription: "M1 MAC PRO", palletCode: "GP1" },
      { msn: "TEST7", itemDescription: "M1 MAC PRO", palletCode: "GP1" },
      { msn: "TEST8", itemDescription: "M1 MAC PRO", palletCode: "GP1" },
    ],
    totalExtraItems: [],
  },
  finalPalletSummary: [
    {
      palletCode: "GP1",
      palletTally: "NO",
      totalItemsAsPerSystem: 5,
      totalItemsScanned: ["TEST9"],
      totalMismatchedItem: 0,
    },
    {
      palletCode: "GP2",
      palletTally: "YES",
      totalItemsAsPerSystem: 0,
      totalItemsScanned: [],
      totalMismatchedItem: 0,
    },
    {
      palletCode: "GP3",
      palletTally: "YES",
      totalItemsAsPerSystem: 0,
      totalItemsScanned: [],
      totalMismatchedItem: 0,
    },
  ],
};

const StockPalletSummary = ({ data = null, onDelete = () => {}, responseData = null, showDelete  = false }) => {
  const theme = useTheme();
  const componentRef = useRef();
  const [deletingSummary, setDeletingSummary] = useState(false);
  const [open, setOpen] = useState(false);
  const [reasonForDelete, setReasonForDelete] = useState("");
  // Handle print functionality
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Stock_Pallet_Summary",
  });


  const handleDeleteSummary = async () => {
      try {
        setDeletingSummary(true);
        await del(URLS.auditReport.deleteSummary + "/" + responseData?._id, { 
          params: {
            reasonForDelete: reasonForDelete,
          }
        });
        onDelete();
        setReasonForDelete("");
        setOpen(false);
      } catch (error) {
        console.log(error);
      } finally {
        setDeletingSummary(false);
      }
  };

  if (!data) return null;

  return (
    <>
    <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
      {/* Print and Delete Buttons */}
      <Box sx={{ mt: 3, textAlign: "right", display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          size="small"
          sx={{
            backgroundColor: theme.palette.primary.main,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          }}
        >
          Print Summary
        </Button>
       { showDelete ?  <ShowComponent module={"Audit Reports"} action="delete">
        <Button
          variant="contained"
          startIcon={<DeleteIcon />}
          onClick={() => setOpen(true)}
          size="small"
          sx={{
            backgroundColor: theme.palette.error.main,
            "&:hover": { backgroundColor: theme.palette.error.dark },
          }}
        >
          Delete Summary
        </Button>

        </ShowComponent> : null}
       
      </Box>
      {/* Printable Area */}
      <Box
        sx={{
          "@media print": {
            padding: "10px 10px",
          },
        }}
        ref={componentRef}
      >
      
        <Box
          sx={{
            mb: 4,
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Stock Summary
          </Typography>

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="caption table">
              <caption>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  alignItems="center"
                >
                  <Typography variant="body1" fontWeight="bold">
                    Total Extra Items:
                  </Typography>

                  {data.stockSummary?.totalExtraItems?.map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      color="info"
                      size="small"
                      variant="filled"
                      icon={<DevicesOtherOutlined />}
                      sx={{ p: 1 }}
                    />
                  ))}
                </Stack>
              </caption>
              <TableHead>
                <TableRow>
                  <TableCell align="left" colSpan={12}>
                    <Typography variant="body1" fontWeight="bold">
                      Stock Match:{" "}
                      <b style={{ color: "green" }}>
                        {" "}
                        {data.stockSummary?.matched}
                      </b>{" "}
                      || Total Items as per System:{" "}
                      <b style={{ color: "green" }}>
                        {" "}
                        {data.stockSummary?.totalItemsAsPerSystem < 10
                          ? `0${data.stockSummary?.totalItemsAsPerSystem}`
                          : data.stockSummary?.totalItemsAsPerSystem}
                      </b>{" "}
                      || Total Items Scanned:{" "}
                      <b style={{ color: "orange" }}>
                        {data.stockSummary?.totalItemsScanned
                          ? `0${data.stockSummary?.totalItemsScanned}`
                          : data.stockSummary?.totalItemsScanned}
                      </b>{" "}
                      || Total Missing Items:{" "}
                      <b style={{ color: "red" }}>
                        {data.stockSummary?.totalMissingItems.length
                          ? `0${data.stockSummary?.totalMissingItems.length}`
                          : data.stockSummary?.totalMissingItems.length}
                      </b>
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left" colSpan={12}>
                    <Typography variant="body1" fontWeight="bold">
                      Missing Items Details
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                  <TableCellStyled align="center">MSN</TableCellStyled>
                  <TableCellStyled align="center">Description</TableCellStyled>
                  <TableCellStyled align="center">Pallet Code</TableCellStyled>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.stockSummary.totalMissingItems.map((row) => (
                  <TableRow key={row.name}>
                    <TableCellStyled component="th" align="center">
                      {row.msn}
                    </TableCellStyled>
                    <TableCellStyled align="center">
                      {row.itemDescription}
                    </TableCellStyled>
                    <TableCellStyled align="center">
                      {row.palletCode}
                    </TableCellStyled>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Final Pallet Summary Table */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
            Final Pallet Summary
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: theme.palette.primary.main }}>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{ color: "#fff", fontWeight: "bold" }}
                  >
                    Pallet Code
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: "#fff", fontWeight: "bold" }}
                  >
                    Pallet Tally
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: "#fff", fontWeight: "bold" }}
                  >
                    Total Items as per System
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: "#fff", fontWeight: "bold" }}
                  >
                    Total Items Scanned
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: "#fff", fontWeight: "bold" }}
                  >
                    Total Mismatched Items
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.finalPalletSummary?.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor:
                        index % 2 === 0
                          ? theme.palette.background.default
                          : theme.palette.action.hover,
                    }}
                  >
                    <TableCell align="center">{row.palletCode}</TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: row.palletTally === "YES" ? "green" : "red",
                        fontWeight: "bold",
                      }}
                    >
                      {row.palletTally}
                    </TableCell>
                    <TableCell align="center">
                      {row.totalItemsAsPerSystem}
                    </TableCell>
                    <TableCell align="center">
                      {row.totalItemsScanned.length > 0
                        ? row.totalItemsScanned.length
                        : "None"}
                    </TableCell>
                    <TableCell align="center">
                      {row.totalMismatchedItem}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
    <DeleteModal loading={deletingSummary} open={open} onClose={() => setOpen(false)} onDeleteClick={handleDeleteSummary} reasonForDelete={reasonForDelete} setReasonForDelete={setReasonForDelete} />
    </>

  );
};

export default StockPalletSummary;
